import React, { ChangeEvent, useState } from 'react'
import {
  AlignItems,
  Body2,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  FormFieldStatus,
  Icon,
  IconVariant,
  InputField,
  JustifyContent,
  SelectField,
  Space,
  Text,
} from '@gousto-internal/citrus-react'

import type { LoggedOutBoxSummaryActions, LoggedOutBoxSummaryDeliveryView } from '../types'
import { PanelContinueButton } from './PanelContinueButton'

type DeliveryPanelProps = {
  view: LoggedOutBoxSummaryDeliveryView
  actions: Pick<
    LoggedOutBoxSummaryActions,
    | 'onDeliveryPostcodeEdit'
    | 'onDeliveryDateSelect'
    | 'onDeliveryInstructionChange'
    | 'onDeliveryInstructionCustomChange'
    | 'onDeliveryContinue'
  >
}

const INTRO_COPY =
  'Our menus change weekly. Please select a date so we can show you the latest recipes.'
const POSTCODE_LABEL = 'Your postcode'
const DATE_LABEL = 'When would you like your first box?'
const INSTRUCTION_LABEL = "Where can we leave your box if you're not in?"
const CUSTOM_INSTRUCTION_LABEL = 'Extra details for your delivery driver'

const DATE_SELECT_ID = 'logged-out-box-summary-delivery-date'
const INSTRUCTION_SELECT_ID = 'logged-out-box-summary-delivery-instruction'
const CUSTOM_INSTRUCTION_ID = 'logged-out-box-summary-delivery-instruction-custom'

const DELIVERY_TIPS = [
  {
    iconName: 'time',
    content: (
      <Text size={2}>
        Receive a{' '}
        <Text size={2} display={Display.Inline} fontFamily={FontFamily.Bold}>
          2-hour delivery slot on the day of delivery via text and email.{' '}
        </Text>
        Delivery will be between 8am - 7pm.
      </Text>
    ),
  },
  {
    iconName: 'cool',
    content: (
      <Text size={2}>
        Insulated packaging keeps your ingredients{' '}
        <Text size={2} display={Display.Inline} fontFamily={FontFamily.Bold}>
          fresh for up to 12 hours.
        </Text>
      </Text>
    ),
  },
  {
    iconName: 'play_pause',
    content: (
      <Text size={2} fontFamily={FontFamily.Bold}>
        No lock in:{' '}
        <Text size={2} display={Display.Inline}>
          pause or cancel for free anytime
        </Text>
      </Text>
    ),
  },
] as const

const WarningCard = ({ title, body, testId }: { title: string; body: string; testId: string }) => (
  <Box
    bg={Color.Warning_50}
    display={Display.Flex}
    paddingH={4}
    paddingV={4}
    alignItems={AlignItems.FlexStart}
    borderColor={Color.Warning_100}
    borderWidth={0.5}
    borderStyle={BorderStyle.Solid}
    borderRadius={1.5}
    gap={2}
    data-testid={testId}
  >
    <Icon name="warning" variant={IconVariant.Error} />
    <Box>
      <Text size={2} fontFamily={FontFamily.Bold}>
        {title}
      </Text>
      <Text size={2}>{body}</Text>
    </Box>
  </Box>
)

export const DeliveryPanel = ({ view, actions }: DeliveryPanelProps) => {
  const [isCustomInstructionTouched, setIsCustomInstructionTouched] = useState(false)

  const { instructions } = view
  const selectedInstruction = instructions.options.find(
    (option) => option.value === instructions.value,
  )
  const requiresCustomInstruction = selectedInstruction?.requiresCustomValue ?? false
  const isCustomInstructionInvalid = instructions.validationMessage !== null
  const showCustomInstructionError = isCustomInstructionTouched && isCustomInstructionInvalid

  const isSubmitDisabled = view.continueCta.isDisabled || view.continueCta.isPending

  const handleDateChange = (event: ChangeEvent<HTMLSelectElement>) => {
    actions.onDeliveryDateSelect(event.target.value)
  }

  // citrus `SelectField` forwards native <select> props but omits `value` from
  // its public types, so we inject it via a spread (same pattern as the Formik
  // `{...field}` usage in account-details). This keeps the dropdown controlled
  // — mirroring control's `DatePicker`, which binds `value={tempDate}` — so it
  // stays in sync when the controller sets the date from a non-user source.
  const controlledDateValue = { value: view.selectedDateId ?? '' }

  const handleInstructionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    actions.onDeliveryInstructionChange(event.target.value)
  }

  const handleCustomInstructionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsCustomInstructionTouched(true)
    actions.onDeliveryInstructionCustomChange(event.target.value)
  }

  const handleContinue = () => {
    if (isSubmitDisabled) {
      return
    }

    actions.onDeliveryContinue()
  }

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={6}
      paddingH={3}
      paddingBottom={6}
      width="100%"
      data-testid="logged-out-box-summary-delivery-panel"
    >
      <Body2 color={Color.ColdGrey_800}>{INTRO_COPY}</Body2>

      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={2}
        width="100%"
        paddingBottom={2}
      >
        <Text size={2} fontFamily={FontFamily.Bold}>
          {POSTCODE_LABEL}
        </Text>
        <Button
          colorVariant={ButtonColorVariant.Secondary}
          width="100%"
          height="48px"
          onClick={actions.onDeliveryPostcodeEdit}
          data-testid="logged-out-box-summary-delivery-postcode-edit"
        >
          <Box
            display={Display.Flex}
            width="100%"
            justifyContent={JustifyContent.SpaceBetween}
            alignItems={AlignItems.Center}
          >
            <Text size={[3, null, 5]} fontFamily={FontFamily.Bold} ellipsis>
              {view.postcodeLabel}
            </Text>
            <Icon size={4} name="edit" variant={IconVariant.Actionable} />
          </Box>
        </Button>
      </Box>

      <SelectField
        id={DATE_SELECT_ID}
        data-testid={DATE_SELECT_ID}
        label={
          <Text size={2} fontFamily={FontFamily.Bold}>
            {DATE_LABEL}
          </Text>
        }
        onChange={handleDateChange}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...controlledDateValue}
      >
        {view.availableDates.map((date) => (
          <option key={date.id} value={date.id} disabled={date.isDisabled}>
            {date.label}
          </option>
        ))}
      </SelectField>

      <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3} width="100%">
        <SelectField
          id={INSTRUCTION_SELECT_ID}
          data-testid={INSTRUCTION_SELECT_ID}
          label={
            <Text size={2} fontFamily={FontFamily.Bold}>
              {INSTRUCTION_LABEL}
            </Text>
          }
          defaultValue={instructions.value}
          onChange={handleInstructionChange}
        >
          <option disabled hidden value="">
            {instructions.placeholder}
          </option>
          {instructions.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>

        {requiresCustomInstruction ? (
          <InputField
            id={CUSTOM_INSTRUCTION_ID}
            data-testid={CUSTOM_INSTRUCTION_ID}
            label={
              <Text size={2} fontFamily={FontFamily.Bold}>
                {CUSTOM_INSTRUCTION_LABEL}
              </Text>
            }
            value={instructions.customValue}
            maxLength={instructions.customConstraints.maxLength}
            onChange={handleCustomInstructionChange}
            status={showCustomInstructionError ? FormFieldStatus.Error : undefined}
            aria-invalid={!isCustomInstructionTouched || isCustomInstructionInvalid}
            validationMessage={
              showCustomInstructionError ? instructions.validationMessage ?? undefined : undefined
            }
          />
        ) : null}
      </Box>

      {view.menuWeekWarning ? (
        <WarningCard
          title={view.menuWeekWarning.title}
          body={view.menuWeekWarning.body}
          testId="logged-out-box-summary-delivery-menu-week-warning"
        />
      ) : null}

      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        data-testid="logged-out-box-summary-delivery-tips"
      >
        {DELIVERY_TIPS.map((tip, index) => (
          <React.Fragment key={tip.iconName}>
            {index > 0 ? <Space size={2} /> : null}
            <Box display={Display.Flex} alignItems={AlignItems.FlexStart}>
              <Icon name={tip.iconName} variant={IconVariant.Informative} />
              <Space size={2} direction="horizontal" />
              {tip.content}
            </Box>
          </React.Fragment>
        ))}
      </Box>

      <PanelContinueButton
        cta={view.continueCta}
        onClick={handleContinue}
        testId="logged-out-box-summary-delivery-submit"
      />
    </Box>
  )
}
